import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { CHURCHES } from '../data/mock';
import { PageHeader } from './MassTimes';
import { useToast } from '../hooks/use-toast';
import { api } from '../lib/api';
import { HoneypotField } from '../components/HoneypotField';
import EditableInline from '../components/EditableInline';

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', website: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.createContact(form);
      toast({ title: 'Message sent', description: 'Thank you for reaching out. We will respond as soon as possible.' });
      setForm({ name: '', email: '', subject: '', message: '', website: '' });
    } catch (err) {
      toast({ title: 'Failed to send', description: err?.response?.data?.detail || 'Please try again shortly.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Get in Touch"
        title="Contact Us"
        subtitle="We&rsquo;d love to hear from you. Reach out with prayer requests, questions, or to schedule a visit with our pastor."
      />

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-5 gap-10">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-[#e9e0cc] p-7">
              <h3 className="font-display text-2xl text-[#2a1a45] mb-4">Parish Office</h3>
              <div className="space-y-3 text-sm text-[#5a534a]">
                <div className="flex items-center gap-3">
                  <Mail size={16} className="text-[#a68a5b]" />
                  <EditableInline
                    as="a"
                    href="mailto:auto"
                    contentKey="office_email"
                    defaultValue="allsaintsparish@rcdvictoria.org"
                    label="Parish office email"
                    className="hover:text-[#3a2359] break-all"
                    placeholder="office@example.com"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Phone size={16} className="text-[#a68a5b]" />
                  <EditableInline
                    as="a"
                    href="tel:auto"
                    contentKey="office_phone"
                    defaultValue="(250) 245-3714"
                    label="Parish office phone"
                    className="hover:text-[#3a2359]"
                    placeholder="(250) 555-0000"
                  />
                </div>
                <div className="pt-2">
                  <span className="text-xs text-[#78716c]">Hours: </span>
                  <EditableInline
                    contentKey="office_hours"
                    defaultValue="Tuesday – Friday, 9:00 AM – 3:00 PM"
                    label="Parish office hours"
                    className="text-xs text-[#78716c]"
                    placeholder="Mon – Fri, 9 AM – 4 PM"
                  />
                </div>
              </div>
            </div>

            {CHURCHES.map(c => (
              <div key={c.id} className="bg-white border border-[#e9e0cc] p-7">
                <p className="text-xs tracking-[0.2em] uppercase text-[#a68a5b] mb-1">{c.community}</p>
                <h3 className="font-display text-2xl text-[#2a1a45] mb-3">{c.name}</h3>
                <div className="space-y-2 text-sm text-[#5a534a]">
                  <div className="flex items-start gap-2">
                    <MapPin size={16} className="mt-0.5 text-[#a68a5b]" />
                    <EditableInline
                      contentKey={`church_${c.id}_address`}
                      defaultValue={c.address}
                      label={`${c.name} address`}
                      placeholder="Street, City, Province Postal"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={16} className="text-[#a68a5b]" />
                    <EditableInline
                      as="a"
                      href="tel:auto"
                      contentKey={`church_${c.id}_phone`}
                      defaultValue={c.phone}
                      label={`${c.name} phone`}
                      className="hover:text-[#3a2359]"
                      placeholder="(250) 555-0000"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-3 bg-white border border-[#e9e0cc] p-8 space-y-5">
        <HoneypotField value={form.website} onChange={(v) => setForm({ ...form, website: v })} />
            <h3 className="font-display text-3xl text-[#2a1a45] mb-2">Send Us a Message</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">Name</label>
                <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-3 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm" />
              </div>
              <div>
                <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">Email</label>
                <input required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-3 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">Subject</label>
              <input required value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} className="w-full px-4 py-3 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm" />
            </div>
            <div>
              <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">Message</label>
              <textarea required rows={6} value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="w-full px-4 py-3 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm resize-none" />
            </div>
            <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-6 py-3 bg-[#3a2359] text-white text-sm tracking-wide hover:bg-[#4d2f75] disabled:opacity-60">
              <Send size={16} /> {submitting ? 'Sending…' : 'Send Message'}
            </button>
          </form>
        </div>
      </section>
    </>
  );
};

export default Contact;
